import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { useMe } from '../api/me';
import { useLiveStatus } from './liveStatusContext';
import { RefreshPill } from './RefreshPill';
import { Skeleton } from './Skeleton';
import { NewJobModal } from './NewJobModal';

// Shared pill styling for the Admin / Me nav links; `active` highlights the
// link for the section currently in view.
function navLinkClass(active: boolean): string {
  return (
    'rounded border px-3 py-1.5 text-sm font-semibold ' +
    (active
      ? 'border-slate-400 bg-slate-100 text-slate-900'
      : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400')
  );
}

export function Header() {
  const { data: me, isPending } = useMe();
  // Read from the shell's single subscription rather than opening one here — see
  // liveStatusContext.ts. The pill's `error` stays null: the pill is a liveness glyph, and
  // the specific failure copy belongs in LiveStatusBanner, which has room for it (A1EE-364).
  const { status } = useLiveStatus();
  const location = useLocation();
  const onMePage = location.pathname === '/me';
  const onAdminPage = location.pathname.startsWith('/admin');
  const [newJobOpen, setNewJobOpen] = useState(false);

  return (
    <header className="flex shrink-0 items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
      <Link to="/" className="text-xl font-semibold text-slate-900 hover:text-slate-700">
        a1ec
      </Link>
      <div className="flex items-center gap-3">
        {isPending || !me ? (
          <Skeleton variant="block" className="h-8 w-36" data-testid="jobs-refresh-pill-skeleton" />
        ) : (
          <RefreshPill kind="sse" status={status ?? 'paused'} error={null} />
        )}
        {!isPending && me && (
          <button
            type="button"
            onClick={() => setNewJobOpen(true)}
            className="rounded border border-sky-600 bg-sky-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-sky-700"
          >
            New job
          </button>
        )}
        {!isPending && me && (
          <Link to="/admin" className={navLinkClass(onAdminPage)}>
            Admin
          </Link>
        )}
        {isPending ? (
          <Skeleton variant="block" className="h-8 w-40" data-testid="header-user-skeleton" />
        ) : me ? (
          <Link
            to="/me"
            title={me.email}
            className={'max-w-[16rem] truncate ' + navLinkClass(onMePage)}
          >
            {me.email}
          </Link>
        ) : null}
      </div>
      {/* Mounted only while open so NewJobModal's useState initializers run each
          time — otherwise a half-typed draft, validation errors, and the page-load
          default session name survive close→reopen (cf. InteractiveSessions.tsx). */}
      {newJobOpen && <NewJobModal open onClose={() => setNewJobOpen(false)} />}
    </header>
  );
}
